// Pure routine/streak logic. Spec section 5 rules:
// - localDate decides which calendar day a session belongs to
// - >= 1 session marks the day completed; extra sessions do not add streak
// - a missed completed previous day resets the streak
// - TODAY does not break the streak until the day has ended
// All functions take `now` so behaviour is testable and timezone-safe.

import type { SessionRecord } from "./storage";
import { toLocalDate } from "./storage";

/** Set of completed local calendar days. */
export function completedDays(sessions: SessionRecord[]): Set<string> {
  return new Set(sessions.map((s) => s.localDate));
}

function shiftDate(base: Date, days: number): Date {
  const d = new Date(base.getFullYear(), base.getMonth(), base.getDate());
  d.setDate(d.getDate() + days);
  return d;
}

/** Monday-first week containing `now`, as 7 local YYYY-MM-DD strings. */
export function currentWeekDays(now: Date = new Date()): string[] {
  const dow = now.getDay(); // 0 = Sunday
  const daysSinceMonday = (dow + 6) % 7;
  const monday = shiftDate(now, -daysSinceMonday);
  return Array.from({ length: 7 }, (_, i) => toLocalDate(shiftDate(monday, i)));
}

/** Sessions whose localDate falls within the Monday-first week of `now`. */
export function getSessionsForWeek(
  sessions: SessionRecord[],
  now: Date = new Date()
): SessionRecord[] {
  const week = new Set(currentWeekDays(now));
  return sessions.filter((s) => week.has(s.localDate));
}

/**
 * Consecutive completed days ending today (if completed) or yesterday.
 * Today being not-yet-completed does NOT reset the streak, because the day
 * has not ended. A gap before yesterday returns 0.
 */
export function getCurrentStreak(
  sessions: SessionRecord[],
  now: Date = new Date()
): number {
  const done = completedDays(sessions);
  const today = toLocalDate(now);
  let cursor: Date;
  if (done.has(today)) {
    cursor = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  } else {
    cursor = shiftDate(now, -1);
    if (!done.has(toLocalDate(cursor))) return 0;
  }
  let streak = 0;
  while (done.has(toLocalDate(cursor))) {
    streak += 1;
    cursor = shiftDate(cursor, -1);
  }
  return streak;
}

/** Most recent session by completedAt, or null. */
export function getLastSession(sessions: SessionRecord[]): SessionRecord | null {
  if (sessions.length === 0) return null;
  return [...sessions].sort((a, b) =>
    a.completedAt < b.completedAt ? 1 : -1
  )[0];
}

export function isTodayCompleted(
  sessions: SessionRecord[],
  now: Date = new Date()
): boolean {
  return completedDays(sessions).has(toLocalDate(now));
}

/** All days of the current calendar month as local YYYY-MM-DD strings. */
export function currentMonthDays(now: Date = new Date()): string[] {
  const y = now.getFullYear();
  const m = now.getMonth();
  const total = new Date(y, m + 1, 0).getDate();
  return Array.from({ length: total }, (_, i) => toLocalDate(new Date(y, m, i + 1)));
}

/** Session count for each given day, in the same order. */
export function sessionsPerDay(
  sessions: SessionRecord[],
  days: string[]
): number[] {
  const counts = new Map<string, number>();
  for (const s of sessions) {
    counts.set(s.localDate, (counts.get(s.localDate) ?? 0) + 1);
  }
  return days.map((d) => counts.get(d) ?? 0);
}
